"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.KnexODataVisitor = exports.tableAliasForNavigationPath = void 0;
const countBy_1 = __importDefault(require("lodash/countBy"));
const identity_1 = __importDefault(require("lodash/identity"));
const knex_odata_functions_1 = require("./knex-odata-functions");
const odata_expression_visitor_1 = require("./odata-expression-visitor");
const OPERATOR_TABLE = {
    gt: '>',
    ge: '>=',
    lt: '<',
    le: '<=',
    ne: '!=',
    eq: '=',
};
function tableAliasForNavigationPath(navigationPath) {
    if (typeof navigationPath === 'string') {
        navigationPath = navigationPath.split('/');
    }
    const navigationCounts = countBy_1.default(navigationPath, identity_1.default);
    const table = navigationPath[navigationPath.length - 1];
    if (!table) {
        return;
    }
    const tableAlias = `${table.toLocaleLowerCase()}_${navigationCounts[table] || 1}`; // author_2, department_1 etc
    return tableAlias;
}
exports.tableAliasForNavigationPath = tableAliasForNavigationPath;
class KnexODataVisitor extends odata_expression_visitor_1.ODataExpressionVisitor {
    constructor(tableName, placeholders) {
        super();
        this.placeholders = placeholders;
        if (tableName) {
            this.listTitle = tableName;
        }
    }
    visitPropertyNode(node, _context) {
        if (!/\//.test(node.name)) {
            if (this.listTitle) {
                return this.listTitle + '.' + node.name;
            }
            return node.name;
        }
        /*
         * These property accessors allow recursive expands by suffixing the amount
         * of times the navigation property appears in a navigation path.
         *
         * Consider the navigation path in the following expression:
         * $filter=Author/Department/Author/Title eq 'Joe Bloggs'.
         *
         * Author is expanded twice, which will generate ambiguous table references
         * if there were two inner join statements generated from the same lookup:
         * Author on the root complex type and Author on Department, so we affix
         * table aliases equal to the amount of times it is referenced in any
         * navigation path:
         *
         * Author is expanded as author_1; Department is expanded as department_1,
         * Author is expanded once again on author_2.
         *
         * Accessing the title property on the second Author expand would use the
         * author_2 suffix as 'Author' appears twice in the navigation path, and
         * accessing the title property on the first expand would use the author_1
         * suffix, as it only appears once in the navigation path, supporting
         * infinitely deep property navigations:
         *
         * /Lists/Tasks ?$filter=Title ne null and Author/Department/Author/Title ne
         * 'System Account' and Author/Department/Title ne 'Sales'
         * &$select=Title,Author/Department/Author/Title,Author/Department/Title,Author/Title
         *
         * would generate the following query:
         *
         * SELECT Tasks.Title, author_2.Title AS [Author/Department/Author/Title],
         * department_1.Title as [Author/Department/Title], author_1.Title as
         * [Author/Title] WHERE Tasks.Title IS NOT NULL AND author_2.Title !=
         * 'System Account' AND department_1 != 'Sales'
         */
        const navigationPath = node.name.split('/');
        const column = navigationPath.pop();
        const tableAlias = tableAliasForNavigationPath(navigationPath);
        return `${tableAlias}.${column}`;
    }
    visitLiteralNode(node) {
        let value = node.value;
        if (value instanceof Array && value[0] === 'null') {
            value = null;
        }
        if (typeof value === 'string') {
            value = value.replace(/(@(.+))/ig, (_match, full, name) => this.placeholders?.[name]?.(node) ?? full);
        }
        return value;
    }
    visitFunctionCallNode(node, context, logic = 'and') {
        const odataFunction = knex_odata_functions_1.odataFunctions[node.func];
        if (!odataFunction) {
            throw new Error(`OData function ${node.func} is not implemented.`);
        }
        const args = node.args.map((arg) => this.visit(arg));
        return odataFunction(logic, ...args)(context);
    }
    visitBinaryExpressionNode(node, context) {
        if (this.isBinaryOperator(node.left) &&
            this.binaryOperatorPresedence(node.left) !==
                this.binaryOperatorPresedence(node)) {
            const self = this;
            node.type === 'and'
                ? context.where(c => {
                    return self.visit(node.left, c /* inner context */);
                })
                : context.orWhere(c => {
                    return self.visit(node.left, c /* inner context */);
                });
        }
        else {
            this.visit(node.left, context, node.type);
        }
        if (this.isBinaryOperator(node.right) &&
            (this.isBinaryOperator(node.left) ||
                this.binaryOperatorPresedence(node.right) <
                    this.binaryOperatorPresedence(node))) {
            const self = this;
            node.type === 'and'
                ? context.where(c => {
                    return self.visit(node.right, c /* inner context */);
                })
                : context.orWhere(c => {
                    return self.visit(node.right, c /* inner context */);
                });
        }
        else {
            this.visit(node.right, context, node.type);
        }
        return context;
    }
    visitConditionalNode(node, context, logic = 'and') {
        const knexOperator = OPERATOR_TABLE[node.type];
        const lvalue = this.visit(node.left, context);
        const rvalue = this.visit(node.right, context);
        /* null has special handling in SQL */
        if (rvalue === null) {
            if (node.type === 'ne') {
                return logic === 'and'
                    ? context.where(c => c.whereNotNull(lvalue))
                    : context.orWhere(c => c.whereNotNull(lvalue));
            }
            return logic === 'and'
                ? context.whereNull(lvalue)
                : context.orWhereNull(lvalue);
        }
        /*
         * SQL removes null values out of not-equal conditional statements.
         *
         * https://stackoverflow.com/questions/11491831/null-values-are-excluded-why
         */
        if (node.type === 'ne') {
            return logic === 'and'
                ? context.where(c => c.where(lvalue, knexOperator, rvalue).orWhere(lvalue, null))
                : context.orWhere(c => c.where(lvalue, knexOperator, rvalue).orWhere(lvalue, null));
        }
        return logic === 'and'
            ? context.where(lvalue, knexOperator, rvalue)
            : context.orWhere(lvalue, knexOperator, rvalue);
    }
}
exports.KnexODataVisitor = KnexODataVisitor;
//# sourceMappingURL=knex-odata-visitor.js.map